import { Component, OnDestroy, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';
import { PageSiteSwitcherComponent } from '../../component/page-site-switcher/page-site-switcher.component';
import { FormPageService, FormPageDto } from '../../core/services/form-page.service';
import { EstablishmentsService } from '../../core/services/establishments.service';
import { SessionService } from '../../core/session.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-formulaire',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmModalComponent, PageSiteSwitcherComponent, TranslatePipe],
  templateUrl: './formulaire.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./formulaire.component.scss'],
})
export class FormulaireComponent implements OnDestroy {
  private readonly api = inject(FormPageService);
  private readonly estApi = inject(EstablishmentsService);
  private readonly session = inject(SessionService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  newPageName = '';
  newPageNameI18n: Record<string, string> = {};
  newQuestion = '';
  newQuestionI18n: Record<string, string> = {};
  pageNameError = '';
  questionError = '';
  maxPageNameLength = 50;
  maxQuestionLength = 200;

  pages = signal<FormPageDto[]>([]);
  selectedIndex = signal<number | null>(null);
  langues = signal<string[]>(['fr']);
  loading = signal(false);

  pendingDeletePage = signal<number | null>(null);
  pendingDeleteQuestion = signal<number | null>(null);

  /** Traductions du nom de la page en cours d'édition */
  currentPageNameI18n: Record<string, string> = {};

  private readonly pageNameChange$ = new Subject<{ name: string; i18n: Record<string, string> }>();

  constructor() {
    effect(() => {
      const estId = this.session.selectedId();
      this.selectedIndex.set(null);
      if (estId) {
        this.loadLangues(estId);
        this.loadPages(estId);
      }
    });

    this.pageNameChange$.pipe(debounceTime(500)).subscribe(({ name, i18n }) => {
      const idx = this.selectedIndex();
      const estId = this.session.selectedId();
      if (idx === null || !estId) return;
      const page = this.pages()[idx];
      this.api.updatePage(estId, page.id, name, i18n).subscribe({
        error: () => this.toast.show(this.translate.instant('formulaire.toast.updateError'), 'error'),
      });
    });
  }

  ngOnDestroy(): void {
    this.pageNameChange$.complete();
  }

  /** Langues secondaires (tout sauf 'fr') */
  get secondaryLangs(): string[] {
    return this.langues().filter(l => l !== 'fr');
  }

  selectedPage() {
    const i = this.selectedIndex();
    return i !== null ? (this.pages()[i] ?? null) : null;
  }

  parseI18n(raw: string | null | undefined): Record<string, string> {
    if (!raw) return {};
    try { return JSON.parse(raw); } catch { return {}; }
  }

  langLabel(lang: string): string {
    return lang === 'ar' ? 'AR' : lang.toUpperCase();
  }

  private loadLangues(establishmentId: string): void {
    this.estApi.getDetails(establishmentId).subscribe({
      next: dto => this.langues.set(dto.langues ?? ['fr']),
    });
  }

  private loadPages(establishmentId: string): void {
    this.loading.set(true);
    this.api.getPages(establishmentId).subscribe({
      next: pages => {
        this.pages.set(pages);
        if (pages.length > 0) {
          this.selectedIndex.set(0);
          this.currentPageNameI18n = this.parseI18n(pages[0].nameI18n);
        }
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  addPage(): void {
    const name = this.newPageName.trim();
    const estId = this.session.selectedId();
    if (!name) { this.pageNameError = this.translate.instant('formulaire.validation.nameRequired'); return; }
    if (name.length > this.maxPageNameLength) { this.pageNameError = this.translate.instant('formulaire.validation.maxChars', { max: this.maxPageNameLength }); return; }
    if (!estId) return;
    this.pageNameError = '';
    const i18n: Record<string, string> = { fr: name, ...this.newPageNameI18n };
    this.api.createPage(estId, name, i18n).subscribe({
      next: page => {
        this.pages.update(p => [...p, page]);
        if (this.selectedIndex() === null) {
          this.selectedIndex.set(0);
          this.currentPageNameI18n = this.parseI18n(page.nameI18n);
        }
        this.newPageName = '';
        this.newPageNameI18n = {};
      },
      error: () => this.toast.show(this.translate.instant('formulaire.toast.createError'), 'error'),
    });
  }

  updatePageName(name: string): void {
    const i = this.selectedIndex();
    if (i === null) return;
    this.pages.update(pages => pages.map((p, idx) => idx === i ? { ...p, name } : p));
    const i18n: Record<string, string> = { fr: name, ...this.currentPageNameI18n };
    this.pageNameChange$.next({ name, i18n });
  }

  updatePageNameI18n(lang: string, value: string): void {
    this.currentPageNameI18n = { ...this.currentPageNameI18n, [lang]: value };
    const page = this.selectedPage();
    if (!page) return;
    const i18n: Record<string, string> = { fr: page.name, ...this.currentPageNameI18n };
    this.pageNameChange$.next({ name: page.name, i18n });
  }

  selectPage(index: number): void {
    this.selectedIndex.set(index);
    this.newQuestion = '';
    this.newQuestionI18n = {};
    const page = this.pages()[index];
    this.currentPageNameI18n = this.parseI18n(page?.nameI18n);
  }

  // ── Suppression page ──────────────────────────────────────────────────────

  removePage(index: number): void {
    this.pendingDeletePage.set(index);
    this.lockScroll();
  }

  confirmDeletePage(): void {
    const index = this.pendingDeletePage();
    const estId = this.session.selectedId();
    if (index === null || !estId) return;
    const page = this.pages()[index];
    this.pendingDeletePage.set(null);
    this.unlockScroll();
    // Supprimer immédiatement de la liste (optimistic)
    if (this.selectedIndex() === index) this.selectedIndex.set(null);
    else if ((this.selectedIndex() ?? 0) > index) this.selectedIndex.update(i => (i ?? 0) - 1);
    this.pages.update(p => p.filter((_, i) => i !== index));
    // Appel API en arrière-plan
    this.api.deletePage(estId, page.id).subscribe({
      error: () => {
        this.toast.show(this.translate.instant('formulaire.toast.deleteError'), 'error');
        this.loadPages(estId);
      },
    });
  }

  cancelDeletePage(): void {
    this.pendingDeletePage.set(null);
    this.unlockScroll();
  }

  // ── Questions ─────────────────────────────────────────────────────────────

  addQuestion(): void {
    const q = this.newQuestion.trim();
    const i = this.selectedIndex();
    const estId = this.session.selectedId();
    if (!q) { this.questionError = this.translate.instant('formulaire.validation.questionRequired'); return; }
    if (q.length > this.maxQuestionLength) { this.questionError = this.translate.instant('formulaire.validation.maxChars', { max: this.maxQuestionLength }); return; }
    if (i === null || !estId) return;
    this.questionError = '';
    const page = this.pages()[i];
    const i18n: Record<string, string> = { fr: q, ...this.newQuestionI18n };
    this.api.addQuestion(estId, page.id, q, i18n).subscribe({
      next: question => {
        this.pages.update(pages =>
          pages.map((p, idx) => idx === i ? { ...p, questions: [...p.questions, question] } : p)
        );
        this.newQuestion = '';
        this.newQuestionI18n = {};
      },
      error: () => this.toast.show(this.translate.instant('formulaire.toast.addError'), 'error'),
    });
  }

  removeQuestion(qIndex: number): void {
    this.pendingDeleteQuestion.set(qIndex);
    this.lockScroll();
  }

  confirmDeleteQuestion(): void {
    const qi = this.pendingDeleteQuestion();
    const i = this.selectedIndex();
    const estId = this.session.selectedId();
    if (qi === null || i === null || !estId) return;
    const page = this.pages()[i];
    const question = page.questions[qi];
    this.pendingDeleteQuestion.set(null);
    this.unlockScroll();
    // Supprimer immédiatement de la liste (optimistic)
    this.pages.update(pages =>
      pages.map((p, idx) => idx === i ? { ...p, questions: p.questions.filter((_, q) => q !== qi) } : p)
    );
    // Appel API en arrière-plan
    this.api.deleteQuestion(estId, page.id, question.id).subscribe({
      error: () => {
        this.toast.show(this.translate.instant('formulaire.toast.deleteError'), 'error');
        this.loadPages(estId);
      },
    });
  }

  cancelDeleteQuestion(): void {
    this.pendingDeleteQuestion.set(null);
    this.unlockScroll();
  }

  private lockScroll(): void { document.body.style.overflow = 'hidden'; }
  private unlockScroll(): void { document.body.style.overflow = ''; }
}